export const EQUIP_COLS = [
  "pump",
  "tank",
  "chem",
  "humg",
  "rscu",
  "emer",
  "heli",
  "control",
  "admin",
  "recovery",
  "satellite",
  "etcar",
  "volunteer",
  "police",
  "military",
  "official",
  "etc",
];

export function parseCellFraction(val) {
  if (val == null || val === "" || val === "0/0") {
    return { vehicles: 0, personnel: 0 };
  }
  const parts = String(val).split("/");
  return {
    vehicles: parseInt(parts[0], 10) || 0,
    personnel: parseInt(parts[1], 10) || 0,
  };
}

export function countTotalVehicles(row, columns = EQUIP_COLS) {
  if (!row) return 0;
  return columns.reduce(
    (acc, col) => acc + parseCellFraction(row[col]).vehicles,
    0,
  );
}

export function computeCellDiff(prevVal, currVal) {
  const prev = parseCellFraction(prevVal);
  const curr = parseCellFraction(currVal);
  const vehiclesDelta = curr.vehicles - prev.vehicles;
  const personnelDelta = curr.personnel - prev.personnel;
  return {
    vehiclesDelta,
    personnelDelta,
    changed: vehiclesDelta !== 0 || personnelDelta !== 0,
    direction:
      vehiclesDelta > 0 ? "up" : vehiclesDelta < 0 ? "down" : "none",
  };
}

export function buildDiffMap(prevRows, currRows, columns = EQUIP_COLS) {
  const prevByOrg = {};
  (prevRows || []).forEach((r) => {
    prevByOrg[r.org || "sum"] = r;
  });
  const map = {};
  (currRows || []).forEach((row) => {
    const org = row.org || "sum";
    const prevRow = prevByOrg[org] || {};
    map[org] = {};
    columns.forEach((col) => {
      map[org][col] = computeCellDiff(prevRow[col], row[col]);
    });
    const prevTotal = countTotalVehicles(prevRow, columns);
    const currTotal = countTotalVehicles(row, columns);
    map[org].total = {
      vehiclesDelta: currTotal - prevTotal,
      personnelDelta: 0,
      changed: currTotal !== prevTotal,
      direction:
        currTotal > prevTotal ? "up" : currTotal < prevTotal ? "down" : "none",
    };
  });
  return map;
}

export function buildSparklineData(seriesCache, date, columns = EQUIP_COLS) {
  if (!seriesCache || !seriesCache[date]) return [];
  const { snapshots, hours } = seriesCache[date];
  return (hours || []).map((h) => {
    const rows = snapshots[String(h)] || [];
    const sumRow = rows.find((r) => r.org === "sum");
    return { hour: h, value: countTotalVehicles(sumRow, columns) };
  });
}

export function computeCountDiff(prevVal, currVal) {
  const prev = Number(prevVal) || 0;
  const curr = Number(currVal) || 0;
  const countDelta = curr - prev;
  return {
    countDelta,
    vehiclesDelta: countDelta,
    changed: countDelta !== 0,
    direction: countDelta > 0 ? "up" : countDelta < 0 ? "down" : "none",
  };
}

export const SIGNBOARD_KEYS = [
  "totl",
  "strt",
  "wait",
  "move",
  "actv",
  "rtrn",
  "support",
];

export function computeSignboardDiff(prev, curr) {
  const map = {};
  SIGNBOARD_KEYS.forEach((key) => {
    map[key] = computeCountDiff(prev?.[key], curr?.[key]);
  });
  return map;
}

export const MF_STATUS_LABEL = {
  9: "집결",
  0: "대기",
  1: "이동",
  2: "활동",
  3: "복귀",
};

export function mfRowKey(row) {
  if (row?.snum != null) return String(row.snum);
  if (row?.seqnum != null) return `seq-${row.seqnum}`;
  return null;
}

/** @returns {Record<string, { changed: boolean, isNew?: boolean, statusFrom?: number, statusTo?: number, label?: string }>} */
export function buildMfListDiffMap(prevRows, nextRows) {
  const prevByKey = {};
  (prevRows || []).forEach((r) => {
    const k = mfRowKey(r);
    if (k) prevByKey[k] = r;
  });
  const map = {};
  (nextRows || []).forEach((row) => {
    const k = mfRowKey(row);
    if (!k) return;
    const prev = prevByKey[k];
    if (!prev) {
      map[k] = { changed: true, isNew: true, statusTo: row.mfstatus };
      return;
    }
    const statusFrom = Number(prev.mfstatus);
    const statusTo = Number(row.mfstatus);
    if (statusFrom !== statusTo) {
      const fromL = MF_STATUS_LABEL[statusFrom] || String(statusFrom);
      const toL = MF_STATUS_LABEL[statusTo] || String(statusTo);
      map[k] = {
        changed: true,
        statusFrom,
        statusTo,
        label: `${fromL}→${toL}`,
      };
    }
  });
  return map;
}

export const DIFF_DISPLAY_MS = 3000;

/** 분 슬라이더용 — 선택 분 이하에서 가장 가까운 스냅샷 분 키 */
export function resolveMinuteSnapshotKey(minuteSeries, minute) {
  const minutes = minuteSeries?.minutes;
  if (!minutes?.length) return null;
  let useMin = minutes[0];
  for (const m of minutes) {
    if (m <= minute) useMin = m;
    else break;
  }
  return useMin;
}

export function resolveMinuteSnapshot(minuteSeries, minute) {
  const key = resolveMinuteSnapshotKey(minuteSeries, minute);
  if (key == null) return [];
  return minuteSeries?.snapshots?.[String(key)] || [];
}

export function resolveMinuteSignboard(minuteSeries, minute) {
  const key = resolveMinuteSnapshotKey(minuteSeries, minute);
  if (key == null) return null;
  return minuteSeries?.signboards?.[String(key)] || null;
}

/** diff 기준 직전 스냅샷 분 (없으면 null → 시간 단위 fallback) */
export function prevMinuteSnapshotKey(minuteSeries, minute) {
  const minutes = minuteSeries?.minutes || [];
  const prev = minutes.filter((m) => m < minute);
  return prev.length ? prev[prev.length - 1] : null;
}

export const TIMELINE_HOURS_LIST = [
  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];

/** 오늘 시간별 리뷰 — 현재 시각 +1시간까지 진행 허용 (그 이후는 종료 구간) */
const REVIEW_END_OFFSET_MS = 60 * 60 * 1000;

export function getTimelineNowBound(isToday, hours = TIMELINE_HOURS_LIST) {
  if (!isToday) {
    return {
      maxHourIdx: hours.length - 1,
      maxMinute: 59,
      hour: hours[hours.length - 1],
      minute: 59,
      endMarkerHour: null,
    };
  }
  const now = new Date();
  const boundTime = new Date(now.getTime() + REVIEW_END_OFFSET_MS);
  const boundHour = boundTime.getHours();
  const boundMinute = boundTime.getMinutes();
  const firstHour = hours[0];
  const lastHour = hours[hours.length - 1];

  if (boundHour < firstHour) {
    return {
      maxHourIdx: 0,
      maxMinute: 0,
      hour: firstHour,
      minute: 0,
      endMarkerHour: firstHour,
    };
  }
  if (boundHour > lastHour || (boundHour === lastHour && boundMinute > 59)) {
    return {
      maxHourIdx: hours.length - 1,
      maxMinute: 59,
      hour: lastHour,
      minute: 59,
      endMarkerHour: null,
    };
  }

  const maxHourIdx = hours.indexOf(boundHour);
  const endMarkerHour =
    boundMinute > 0 && boundHour < lastHour ? boundHour + 1 : null;

  return {
    maxHourIdx: maxHourIdx >= 0 ? maxHourIdx : hours.length - 1,
    maxMinute: boundMinute,
    hour: boundHour,
    minute: boundMinute,
    endMarkerHour,
  };
}

export function clampTimelineSelection(
  hour,
  minute,
  isToday,
  hours = TIMELINE_HOURS_LIST,
) {
  const bound = getTimelineNowBound(isToday, hours);
  let h = hour ?? hours[0];
  let m = minute ?? 0;
  if (!isToday) return { hour: h, minute: m };

  if (h < hours[0]) {
    return { hour: hours[0], minute: 0 };
  }
  const hIdx = hours.indexOf(h);
  if (hIdx < 0 || hIdx > bound.maxHourIdx) {
    h = hours[bound.maxHourIdx];
    m = bound.maxMinute;
    return { hour: h, minute: m };
  }
  if (hIdx === bound.maxHourIdx && m > bound.maxMinute) {
    m = bound.maxMinute;
  }
  return { hour: h, minute: m };
}
